import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  AlertTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  createTheme,
  Theme,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";
import { HeatStatusPill } from "components/heatQueue/HeatStatusPill";
import { MeltPlanStaleAlert } from "components/heatQueue/MeltPlanFreshness";
import { useFormatOverrideRequested } from "components/heatQueue/override/useFormatOverrideRequested";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  HeatQueueEntry,
  HeatQueueStatus,
  useGetHeatQueueQuery,
} from "src/store/api/generatedApi";
import { useHeatChargeProgress } from "./useHeatChargeProgress";
import { MixDisplayTable } from "./mixChangeAcknowledgment/MixDisplayTable";

// Matches the melt-plan feed's cache lifetime; polling faster cannot see new data.
const POLLING_INTERVAL_MS = 60_000;
const RECIPE_FONT_SIZE = 48;
const MAX_HEAT_TABS = 5;
const CRANE_TEXT_SCALE = 1.6;
const HEAT_ID_PARAM = "heatId";
const LOADING_HEAT_ID_PARAM = "loadingHeatId";

const heatIdParamValue = (heatId: number | null) => heatId?.toString() ?? "";

const scaleFontSize = (fontSize: string | number) =>
  typeof fontSize === "number"
    ? fontSize * CRANE_TEXT_SCALE
    : `calc(${fontSize} * ${CRANE_TEXT_SCALE.toString()})`;

const withCraneTextScale = (theme: Theme) =>
  createTheme(theme, {
    typography: Object.fromEntries(
      Object.entries(theme.typography).flatMap(
        ([variant, style]: [string, unknown]) =>
          typeof style === "object" &&
          style !== null &&
          "fontSize" in style &&
          (typeof style.fontSize === "number" ||
            typeof style.fontSize === "string")
            ? [[variant, { fontSize: scaleFontSize(style.fontSize) }]]
            : []
      )
    ),
  });

const HeatFact = ({
  label,
  value,
  pillStatus,
}: {
  label: string;
  value: string;
  pillStatus?: HeatQueueStatus;
}) => (
  <Stack>
    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      {pillStatus !== undefined && <HeatStatusPill status={pillStatus} />}
    </Stack>
    <Typography variant="h4">{value}</Typography>
  </Stack>
);

const overrideCause = (reason: string | null, subreason: string | null) =>
  [reason, subreason].filter((part) => part !== null).join(" — ");

const displayedHeats = (heats: HeatQueueEntry[]) =>
  heats.slice(0, MAX_HEAT_TABS);

const recipeName = (heat: HeatQueueEntry, noRecipeLabel: string) =>
  heat.recipe?.name ?? heat.recipe_name ?? noRecipeLabel;

const TabValue = ({
  children,
  color,
  fontWeight,
}: {
  children: string;
  color?: "warning.main";
  fontWeight?: "fontWeightBold";
}) => (
  <Typography
    variant="body1"
    noWrap
    sx={{ width: "100%", textAlign: "left", color, fontWeight }}
  >
    {children}
  </Typography>
);

const HeatRecipeTabs = ({
  heats,
  selectedHeatId,
  onChange,
}: {
  heats: HeatQueueEntry[];
  selectedHeatId: number;
  onChange: (heatId: number) => void;
}) => {
  const { t } = useTenantTranslation();

  return (
    <Tabs
      value={selectedHeatId}
      onChange={(_event, heatId: number) => {
        onChange(heatId);
      }}
      variant="scrollable"
      scrollButtons="auto"
      aria-label={t("heatQueue")}
      sx={{
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: "grey.300",
        "& .MuiTabs-flexContainer": { gap: 1 },
      }}
    >
      {heats.map((heat) => {
        const isMelting = heat.status === "melting";
        return (
          <Tab
            key={heat.heat_id}
            id={`heat-tab-${heat.heat_id.toString()}`}
            aria-controls={`heat-panel-${heat.heat_id.toString()}`}
            value={heat.heat_id}
            label={
              <Stack sx={{ width: "100%", gap: 0.5 }}>
                <TabValue
                  color={isMelting ? "warning.main" : undefined}
                  fontWeight="fontWeightBold"
                >
                  {`${isMelting ? t("heatStatusMelting") : t("heat")} ${heat.heat_id.toString()}`}
                </TabValue>
                <TabValue>{heat.steel_grade}</TabValue>
                <TabValue>{recipeName(heat, t("noRecipeAvailable"))}</TabValue>
              </Stack>
            }
            sx={{
              flex: "1 0 13.5rem",
              maxWidth: "none",
              minHeight: 0,
              alignItems: "stretch",
              px: 1.5,
              py: 1,
              color: "text.secondary",
              textTransform: "none",
              border: 1,
              borderBottom: 0,
              borderColor: "grey.300",
              borderTopWidth: isMelting ? 3 : 1,
              borderTopColor: isMelting ? "warning.main" : "grey.300",
              borderTopLeftRadius: 1,
              borderTopRightRadius: 1,
              backgroundColor: "grey.50",
              "&.Mui-selected": {
                color: "text.primary",
                backgroundColor: "common.white",
                borderColor: "primary.main",
                borderTopColor: isMelting ? "warning.main" : "primary.main",
              },
            }}
          />
        );
      })}
    </Tabs>
  );
};

const CraneHeatRecipesContent = () => {
  const { t } = useTenantTranslation();
  const formatOverrideRequested = useFormatOverrideRequested();
  const tenantName = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: heatQueue, isLoading } = useGetHeatQueueQuery(
    { tenantName },
    { pollingInterval: POLLING_INTERVAL_MS }
  );
  const currentHeatId = heatQueue?.current_heat_id ?? null;
  const heats = displayedHeats(heatQueue?.heats ?? []);
  const defaultHeat =
    heats.find((heat) => heat.heat_id === currentHeatId) ?? heats[0] ?? null;
  const pickedDuringCurrentLoading =
    searchParams.get(LOADING_HEAT_ID_PARAM) === heatIdParamValue(currentHeatId);
  const pickedHeat = pickedDuringCurrentLoading
    ? heats.find(
        (heat) =>
          heatIdParamValue(heat.heat_id) === searchParams.get(HEAT_ID_PARAM)
      )
    : undefined;
  const selectedHeat = pickedHeat ?? defaultHeat;

  const chargeProgress = useHeatChargeProgress({
    tenantName,
    stableHeatId: selectedHeat?.stable_heat_id ?? null,
    mixId: selectedHeat?.recipe?.mix.id ?? null,
  });

  const pickHeat = (heatId: number) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(HEAT_ID_PARAM, heatIdParamValue(heatId));
        next.set(LOADING_HEAT_ID_PARAM, heatIdParamValue(currentHeatId));
        return next;
      },
      { replace: true }
    );
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={320} />;
  }

  if (heatQueue === undefined) {
    return <Alert severity="error">{t("heatQueueLoadError")}</Alert>;
  }

  const staleAlert = (
    <MeltPlanStaleAlert planUpdatedAt={heatQueue.plan_updated_at} />
  );

  if (selectedHeat === null) {
    return (
      <Stack sx={{ gap: 3, width: "100%" }}>
        {staleAlert}
        <Alert severity="warning">{t("heatQueueEmpty")}</Alert>
      </Stack>
    );
  }

  const { recipe } = selectedHeat;
  const override =
    heatQueue.active_overrides.find(
      (activeOverride) =>
        activeOverride.override_id === selectedHeat.override_id
    ) ?? null;
  const overrideCauseLabel =
    override === null ? "" : overrideCause(override.reason, override.subreason);

  return (
    <Stack sx={{ gap: 2, width: "100%", flexGrow: 1, minHeight: 0 }}>
      {staleAlert}
      <HeatRecipeTabs
        heats={heats}
        selectedHeatId={selectedHeat.heat_id}
        onChange={pickHeat}
      />

      <Stack
        role="tabpanel"
        id={`heat-panel-${selectedHeat.heat_id.toString()}`}
        aria-labelledby={`heat-tab-${selectedHeat.heat_id.toString()}`}
        sx={{ gap: 2, flexGrow: 1, minHeight: 0 }}
      >
        <Stack direction="row" sx={{ gap: 6, alignItems: "flex-end" }}>
          {recipe !== null && (
            <Typography
              variant="h5"
              sx={{ color: "primary.main", fontSize: RECIPE_FONT_SIZE }}
            >
              {recipe.name}
            </Typography>
          )}
          <HeatFact
            label={t("heat")}
            value={selectedHeat.heat_id.toString()}
            pillStatus={
              selectedHeat.status === "melting" ? "melting" : undefined
            }
          />
          <HeatFact label={t("steelGrade")} value={selectedHeat.steel_grade} />
        </Stack>

        {override !== null && (
          <Alert
            severity="warning"
            icon={false}
            sx={{ maxWidth: "max-content" }}
          >
            <AlertTitle
              sx={{
                typography: "h6",
                fontWeight: "fontWeightBold",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WarningAmber fontSize="inherit" />
              {t("overrideActiveHeading")}
            </AlertTitle>
            <Stack sx={{ gap: 0.5 }}>
              {overrideCauseLabel !== "" && (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "fontWeightMedium" }}
                >
                  {overrideCauseLabel}
                </Typography>
              )}
              {override.comment !== null && (
                <Typography variant="body1">{override.comment}</Typography>
              )}
              <Typography variant="body1">
                {formatOverrideRequested(override)}
              </Typography>
            </Stack>
          </Alert>
        )}

        {recipe === null ? (
          <Alert severity="info">{t("noRecipeAvailable")}</Alert>
        ) : (
          <MixDisplayTable
            key={`${selectedHeat.heat_id.toString()}:${recipe.mix.id.toString()}`}
            mix={recipe.mix}
            chargeProgress={chargeProgress}
          />
        )}
      </Stack>
    </Stack>
  );
};

export const CraneHeatRecipes = () => {
  const theme = useTheme();
  const craneTheme = useMemo(() => withCraneTextScale(theme), [theme]);
  return (
    <ThemeProvider theme={craneTheme}>
      <CraneHeatRecipesContent />
    </ThemeProvider>
  );
};
